/**
 * Unified Data Page
 *
 * Consolidates Knowledge Base, Knowledge Graphs, and AMLKG Memory into a single
 * project-centric page with tabbed navigation.
 */
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  CircleStackIcon,
  ArrowPathIcon,
  BoltIcon,
  ClipboardDocumentIcon,
  PlayIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import Button from '@/components/common/Button'
import { apiClient } from '@/services/api'
import {
  ProjectSelector,
  DataOverviewTab,
  KnowledgeGraphsTab,
  CollectionsTab,
  MemoryTab,
  AgentWritesTab,
  PIISimulationTab,
} from '@/components/data'
import { dataStore } from '@/stores/DataStore'
import clsx from 'clsx'
import toast from 'react-hot-toast'

type TabType = 'overview' | 'graphs' | 'collections' | 'memory' | 'writes' | 'neuralgraph' | 'pii-sim'

interface TabConfig {
  id: TabType
  label: string
  path: string
}

const TABS: TabConfig[] = [
  { id: 'overview', label: 'Overview', path: '/data' },
  { id: 'graphs', label: 'Knowledge Graphs', path: '/data/graphs' },
  { id: 'collections', label: 'Collections', path: '/data/collections' },
  { id: 'memory', label: 'Memory', path: '/data/memory' },
  { id: 'writes', label: 'Agent Writes', path: '/data/writes' },
  { id: 'pii-sim', label: 'PII Simulation', path: '/data/pii-sim' },
  { id: 'neuralgraph' as TabType, label: 'UES Explorer', path: '/data/neuralgraph' },
]

interface DataPageProps {
  tab?: TabType
}

const DataPage: React.FC<DataPageProps> = observer(({ tab }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Determine active tab from route
  const getActiveTab = (): TabType => {
    if (tab) return tab
    if (location.pathname === '/data/graphs') return 'graphs'
    if (location.pathname === '/data/collections') return 'collections'
    if (location.pathname === '/data/memory') return 'memory'
    if (location.pathname === '/data/writes') return 'writes'
    if (location.pathname === '/data/pii-sim') return 'pii-sim'
    if (location.pathname === '/data/neuralgraph') return 'neuralgraph'
    return 'overview'
  }

  const activeTab = getActiveTab()

  // Handle project ID from query params
  useEffect(() => {
    const projectId = searchParams.get('projectId')
    if (projectId && projectId !== dataStore.selectedProjectId) {
      dataStore.setSelectedProjectId(projectId)
    }
  }, [searchParams])

  // Update query params when project changes
  useEffect(() => {
    const currentProjectId = searchParams.get('projectId')
    if (dataStore.selectedProjectId !== currentProjectId) {
      if (dataStore.selectedProjectId) {
        setSearchParams({ projectId: dataStore.selectedProjectId })
      } else if (currentProjectId) {
        searchParams.delete('projectId')
        setSearchParams(searchParams)
      }
    }
  }, [dataStore.selectedProjectId])

  // Initial data fetch
  useEffect(() => {
    dataStore.fetchProjects()
  }, [])

  const handleTabChange = (newTab: TabType) => {
    const tabConfig = TABS.find((t) => t.id === newTab)
    if (tabConfig) {
      const params = dataStore.selectedProjectId
        ? `?projectId=${dataStore.selectedProjectId}`
        : ''
      navigate(`${tabConfig.path}${params}`)
    }
  }

  const handleRefresh = async () => {
    await dataStore.refresh()
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'graphs':
        return <KnowledgeGraphsTab />
      case 'collections':
        return <CollectionsTab />
      case 'memory':
        return <MemoryTab />
      case 'writes':
        return <AgentWritesTab />
      case 'pii-sim':
        return <PIISimulationTab />
      case 'neuralgraph':
        return <UESExplorerTab />
      case 'overview':
      default:
        return <DataOverviewTab />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CircleStackIcon className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Data</h1>
            <p className="text-sm text-muted-foreground">
              Manage knowledge graphs, collections, and agent memory
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={handleRefresh}>
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <ProjectSelector />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {TABS.map((tabConfig) => (
            <button
              key={tabConfig.id}
              onClick={() => handleTabChange(tabConfig.id)}
              className={clsx(
                'py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors',
                activeTab === tabConfig.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tabConfig.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>{renderTabContent()}</div>
    </div>
  )
})

DataPage.displayName = 'DataPage'

// ── UES Explorer Tab ─────────────────────────────
//
// Replaces the misnamed "NeuralGraph" tab (which was actually surfacing local
// AMLKG summary). This is an interactive UES query explorer: pick an intent,
// type a query, see the extraction's subject + confidence + context document
// + graph nodes/edges. Recent + favourite queries persist in localStorage.

type UESIntent = 'inspect' | 'evolve' | 'create' | 'audit'

interface RecentQuery {
  intent: UESIntent
  query: string
  ts: number
  confidence?: number
  ok?: boolean
}

const RECENT_KEY = 'ues-explorer-recent'
const FAVS_KEY = 'ues-explorer-favorites'
const RECENT_LIMIT = 10

function loadJson<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return v ? (JSON.parse(v) as T) : fallback
  } catch {
    return fallback
  }
}

function saveJson(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* ignore */ }
}

function ConfidenceGauge({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value || 0))
  const color = pct >= 0.75 ? 'stroke-emerald-500' : pct >= 0.5 ? 'stroke-amber-500' : 'stroke-red-500'
  const label = pct >= 0.75 ? 'high' : pct >= 0.5 ? 'medium' : 'low'
  // Simple radial: r=18, circumference ≈ 113.1
  const C = 2 * Math.PI * 18
  const dash = pct * C
  return (
    <div className="flex flex-col items-center justify-center w-20 h-20">
      <svg viewBox="0 0 44 44" className="w-16 h-16 -rotate-90">
        <circle cx="22" cy="22" r="18" className="stroke-muted fill-none" strokeWidth="4" />
        <circle
          cx="22" cy="22" r="18"
          className={clsx('fill-none transition-all', color)}
          strokeWidth="4"
          strokeDasharray={`${dash} ${C}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="text-xs font-semibold text-foreground -mt-12">{(pct * 100).toFixed(0)}%</div>
      <div className="text-[10px] text-muted-foreground mt-9">{label}</div>
    </div>
  )
}

function UESExplorerTab() {
  const [intent, setIntent] = useState<UESIntent>('inspect')
  const [query, setQuery] = useState('')
  const [contextJson, setContextJson] = useState('')
  const [recents, setRecents] = useState<RecentQuery[]>(() => loadJson(RECENT_KEY, []))
  const [favorites, setFavorites] = useState<RecentQuery[]>(() => loadJson(FAVS_KEY, []))

  const projectId = dataStore.selectedProjectId || undefined

  const extract = useMutation({
    mutationFn: async () => {
      let parsedCtx: Record<string, any> | undefined
      if (contextJson.trim()) {
        try {
          parsedCtx = JSON.parse(contextJson)
        } catch {
          throw new Error('Context must be valid JSON')
        }
      }
      return apiClient.neuralGraphExtract({
        intent,
        query: query.trim(),
        context: parsedCtx,
        project_id: projectId,
      })
    },
    onSuccess: (data) => {
      const entry: RecentQuery = {
        intent,
        query: query.trim(),
        ts: Date.now(),
        confidence: data.confidence,
        ok: data.success,
      }
      const next = [entry, ...recents.filter(r => !(r.intent === intent && r.query === query.trim()))].slice(0, RECENT_LIMIT)
      setRecents(next)
      saveJson(RECENT_KEY, next)
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Extract failed')
    },
  })

  const result = extract.data

  const handleRun = () => {
    if (!query.trim()) {
      toast.error('Enter a query first')
      return
    }
    extract.mutate()
  }

  const replay = (r: RecentQuery) => {
    setIntent(r.intent)
    setQuery(r.query)
  }

  const toggleFavorite = (r: RecentQuery) => {
    const exists = favorites.some(f => f.intent === r.intent && f.query === r.query)
    const next = exists
      ? favorites.filter(f => !(f.intent === r.intent && f.query === r.query))
      : [r, ...favorites].slice(0, 20)
    setFavorites(next)
    saveJson(FAVS_KEY, next)
  }

  const isFav = (r: RecentQuery) =>
    favorites.some(f => f.intent === r.intent && f.query === r.query)

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`))
  }

  const meta = result?.metadata || {}
  const graph = result?.graph || { nodes: [], edges: [], paths: [] }

  return (
    <div className="space-y-6">
      {/* Query controls */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <BoltIcon className="w-5 h-5 text-amber-500" />
          UES Explorer
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            Query NeuralGraph via UES extract
          </span>
        </h3>

        <div className="flex items-start gap-3">
          <div className="w-32 shrink-0">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Intent</label>
            <select
              value={intent}
              onChange={e => setIntent(e.target.value as UESIntent)}
              className="w-full text-sm px-2 py-2 rounded-md border border-border bg-card"
            >
              <option value="inspect">inspect</option>
              <option value="evolve">evolve</option>
              <option value="create">create</option>
              <option value="audit">audit</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Query</label>
            <textarea
              value={query}
              onChange={e => setQuery(e.target.value)}
              rows={2}
              placeholder="e.g. sales-playbook objection handling for SMB CFOs"
              className="w-full text-sm px-3 py-2 rounded-md border border-border bg-card font-mono resize-none"
            />
          </div>
          <div className="w-28 shrink-0 flex flex-col justify-end">
            <Button onClick={handleRun} disabled={extract.isPending || !query.trim()} className="w-full">
              {extract.isPending ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
              <span className="ml-1.5">{extract.isPending ? 'Running…' : 'Run'}</span>
            </Button>
          </div>
        </div>

        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Context (JSON) — optional
          </summary>
          <textarea
            value={contextJson}
            onChange={e => setContextJson(e.target.value)}
            rows={3}
            placeholder='{"source":"data-page","scope":"current-project"}'
            className="mt-2 w-full text-xs px-3 py-2 rounded-md border border-border bg-card font-mono resize-y"
          />
        </details>

        {projectId && (
          <p className="text-[10px] text-muted-foreground">
            Scoped to project <span className="font-mono">{projectId.slice(0, 8)}…</span>
          </p>
        )}
      </div>

      {/* Result panel */}
      {extract.isError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-600">
          {(extract.error as any)?.message || 'Extract failed'}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* Header row: subject + confidence + metadata */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Subject</p>
              <p className="text-base font-semibold text-foreground break-words">
                {typeof result.subject === 'string' ? result.subject : (result.subject ? JSON.stringify(result.subject) : '—')}
              </p>
              {result.extraction_id && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] text-muted-foreground">extraction_id</span>
                  <code className="text-[10px] font-mono text-foreground bg-muted px-1.5 py-0.5 rounded">{result.extraction_id}</code>
                  <button
                    onClick={() => copyToClipboard(result.extraction_id!, 'Extraction ID')}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ClipboardDocumentIcon className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                <span className={result.success ? 'text-emerald-600' : 'text-red-500'}>
                  {result.success ? 'success' : 'failed'}
                </span>
                {meta.degraded && <span className="text-amber-500">degraded</span>}
                {meta.sources_consulted != null && <span>{meta.sources_consulted} sources</span>}
                {meta.latency_ms != null && <span>{meta.latency_ms} ms</span>}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 flex items-center justify-center gap-4">
              <ConfidenceGauge value={result.confidence || 0} />
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">Confidence</p>
                <p>0 = no relevant context found</p>
                <p>1 = strong match</p>
              </div>
            </div>
          </div>

          {/* Context document */}
          {result.context_document && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Context document</p>
                <button
                  onClick={() => copyToClipboard(result.context_document, 'Context document')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap break-words font-mono bg-muted/40 rounded p-3 max-h-96 overflow-y-auto">
                {result.context_document}
              </pre>
            </div>
          )}

          {/* Graph nodes/edges (lightweight list view; ZiroGraphDiagram swap is a future polish) */}
          {(graph.nodes.length > 0 || graph.edges.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
                  Graph nodes ({graph.nodes.length})
                </p>
                <div className="space-y-1 max-h-64 overflow-y-auto text-xs">
                  {graph.nodes.slice(0, 50).map((n: any, i: number) => (
                    <div key={i} className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/30">
                      <span className="font-medium text-foreground truncate">{n.name || n.id || JSON.stringify(n).slice(0, 60)}</span>
                      {n.layer && <span className="text-[10px] text-muted-foreground ml-2">{n.layer}</span>}
                    </div>
                  ))}
                  {graph.nodes.length > 50 && (
                    <p className="text-[10px] text-muted-foreground text-center pt-1">+{graph.nodes.length - 50} more</p>
                  )}
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
                  Graph edges ({graph.edges.length})
                </p>
                <div className="space-y-1 max-h-64 overflow-y-auto text-xs">
                  {graph.edges.slice(0, 50).map((e: any, i: number) => (
                    <div key={i} className="py-1 px-2 rounded hover:bg-muted/30 font-mono text-[11px] text-foreground truncate">
                      {(e.source || e.from || '?')}
                      <span className="text-muted-foreground mx-1">{e.type || e.label || '→'}</span>
                      {(e.target || e.to || '?')}
                    </div>
                  ))}
                  {graph.edges.length > 50 && (
                    <p className="text-[10px] text-muted-foreground text-center pt-1">+{graph.edges.length - 50} more</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recents + favorites */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentList
          title="Recent queries"
          items={recents}
          onReplay={replay}
          onToggleFav={toggleFavorite}
          isFav={isFav}
          onClear={() => { setRecents([]); saveJson(RECENT_KEY, []) }}
        />
        <RecentList
          title="Favourites"
          items={favorites}
          onReplay={replay}
          onToggleFav={toggleFavorite}
          isFav={isFav}
          onClear={() => { setFavorites([]); saveJson(FAVS_KEY, []) }}
        />
      </div>
    </div>
  )
}

interface RecentListProps {
  title: string
  items: RecentQuery[]
  onReplay: (r: RecentQuery) => void
  onToggleFav: (r: RecentQuery) => void
  isFav: (r: RecentQuery) => boolean
  onClear: () => void
}

function RecentList({ title, items, onReplay, onToggleFav, isFav, onClear }: RecentListProps) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</p>
        {items.length > 0 && (
          <button
            onClick={onClear}
            className="text-[10px] text-muted-foreground hover:text-red-500 inline-flex items-center gap-0.5"
          >
            <TrashIcon className="w-3 h-3" /> clear
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">No entries yet</p>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {items.map((r, i) => (
            <li key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/30 text-xs">
              <span className="text-[10px] uppercase font-semibold text-blue-500 w-12 shrink-0">{r.intent}</span>
              <button onClick={() => onReplay(r)} className="flex-1 text-left text-foreground truncate hover:underline">
                {r.query}
              </button>
              {r.confidence != null && (
                <span className={clsx(
                  'text-[10px] tabular-nums',
                  r.confidence >= 0.75 ? 'text-emerald-500'
                    : r.confidence >= 0.5 ? 'text-amber-500'
                    : 'text-red-500',
                )}>
                  {(r.confidence * 100).toFixed(0)}%
                </span>
              )}
              <button onClick={() => onToggleFav(r)} className="text-muted-foreground hover:text-amber-500 shrink-0">
                {isFav(r) ? <StarSolidIcon className="w-3.5 h-3.5 text-amber-500" /> : <StarIcon className="w-3.5 h-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default DataPage
